import { animate, animation, keyframes, style, transition, trigger } from '@angular/animations';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { interval, merge, Observable, Subject, Subscription } from 'rxjs';
import { map, share, skipWhile, take } from 'rxjs/operators';
import { useAnimationIncludingChildren } from 'src/app/shared/animations/zoom-out-right.animation';
import { BasicService } from 'src/app/shared/services/basic.service';
import { SuggestAnswerService } from 'src/app/shared/services/suggest-answer.service';
import { AIAssistantAnswer } from 'src/app/task-process/shared/models/ai-assistant-answer.model';

import { CodemirrorContext } from '../codemirror-context';

@Component({
  selector: 'app-ai-assistant-suggestion',
  templateUrl: './ai-assistant-suggestion.component.html',
  styleUrls: ['./ai-assistant-suggestion.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeOutDown', [
      transition(
        '0 => 1',
        [
          ...useAnimationIncludingChildren(
            animation([
              animate(
                '{{duration}}ms {{delay}}ms',
                keyframes([
                  style({ opacity: 1, transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 0 }),
                  style({ opacity: 0, transform: 'translate3d(0, {{translate}}, 0)', easing: 'ease', offset: 1 }),
                ])
              ),
            ])
          ),
        ],
        {
          params: {
            delay: 1,
            duration: 500,
            translate: '100%',
          },
        }
      ),
    ]),
  ],
})
export class AiAssistantSuggestionComponent implements OnInit, OnDestroy {
  @Input() codemirrorContext: CodemirrorContext;
  @Input() taskId: number;
  @Output() answerAccepted: EventEmitter<AIAssistantAnswer> = new EventEmitter();
  @Output() answerRejected: EventEmitter<AIAssistantAnswer> = new EventEmitter();

  isPopoverDisplayed$: Observable<boolean>;
  suggestion$: Observable<AIAssistantAnswer>;
  private dismissed$ = new Subject<void>();

  subscription = new Subscription();
  animationState = 0;

  @HostListener('window:resize')
  onResize() {
    const editorElement = document.querySelector('app-codemirror-wrapper');
    const suggestionContainerElement: any = document.querySelector('app-ai-assistant-suggestion');
    // Copies and force the width with the one of the editor.
    suggestionContainerElement.style.cssText += `width:${editorElement.getBoundingClientRect().width}px;`;
  }

  constructor(public basicService: BasicService, private suggestAnswerService: SuggestAnswerService) {}

  ngOnInit() {
    this.subscription = interval(100)
      .pipe(
        skipWhile(() => {
          let result = null;
          try {
            result = document.querySelector('app-ai-assistant-suggestion');
          } catch {
            result = null;
          }
          return !result;
        }),
        take(1)
      )
      .subscribe({
        next: () => {
          this.onResize();
          this.initSearch();
          this.initIsDisplayed();
        },
      });
  }

  emitAcceptedAIAssistantAnswer(answer: AIAssistantAnswer) {
    this.basicService.resetAIAssistantAnswerForTaskId(this.taskId);
    this.answerAccepted.emit(answer);
    this.dismissed$.next();
  }

  rejectAIAssistantAnswer(answer: AIAssistantAnswer) {
    this.basicService.resetAIAssistantAnswerForTaskId(this.taskId);
    this.answerRejected.emit(answer);
    this.dismissed$.next();
  }

  minimizeAIAssistantAnswer() {
    this.animationState = 1;
    this.suggestAnswerService.minimizeAIAssistantAnswer(this.taskId);
    setTimeout(() => {
      this.animationState = 0;
      this.dismissed$.next();
    }, 450); // The timeout must be smaller than the animation duration.
  }

  private initSearch() {
    this.suggestion$ = this.basicService.answerSuggestionPending[this.taskId]?.resultBS.asObservable();
  }

  private initIsDisplayed() {
    const falseWhenDismissed$ = merge(this.dismissed$, this.codemirrorContext.escapePressed$).pipe(map(() => false));
    const hasSomeSuggestions$ = this.suggestion$.pipe(map(sugestion => !!sugestion.answer));

    this.isPopoverDisplayed$ = merge(falseWhenDismissed$, hasSomeSuggestions$).pipe(share());
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
